import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Die Suchseite.
 *
 * Die **`suppression`**-Sätze sind die sichtbare Hälfte einer Messung und der Grund, warum eine Reihe von
 * Bedienelementen ausgegraut vor dem Leser steht: `channel:`, `user:` und `subscriptions:` werden als
 * Sonderfälle beantwortet und nicht als Suchen, und eines davon mit einem Filter zu verbinden ignoriert
 * den Filter nicht, es *verdirbt* die Antwort.
 *
 * Die **`duration`**-Angaben sind gemessen und nicht aus einer Hilfeseite: `short` ist alles unter 240 s,
 * `medium` 240–507 s, `long` ab 2 183 s. Der Halbgeviertstrich in `4–20 Minuten` bleibt, weil er eine
 * Spanne bezeichnet.
 */
export const search: Strings['search'] = {
  type: {
    all: 'Alles',
    video: 'Videos',
    channel: 'Kanäle',
    playlist: 'Playlists',
    movie: 'Filme',
  },

  sort: { relevance: 'Relevanz', views: 'Aufrufe' },

  date: {
    any: 'Jederzeit',
    hour: 'Letzte Stunde',
    today: 'Heute',
    week: 'Diese Woche',
    month: 'Diesen Monat',
    year: 'Dieses Jahr',
  },

  duration: {
    any: 'Beliebige Länge',
    short: 'Kurz',
    medium: 'Mittel',
    long: 'Lang',
    shortDetail: 'unter 4 Minuten',
    mediumDetail: '4–20 Minuten',
    longDetail: 'über 20 Minuten',
  },

  feature: {
    hd: 'HD',
    subtitles: 'Untertitel / CC',
    creativeCommons: 'Creative Commons',
    threeD: '3D',
    live: 'Live',
    purchased: 'Gekauft',
    fourK: '4K',
    threeSixty: '360°',
    location: 'Ort',
    hdr: 'HDR',
  },

  anyValue: 'Beliebig',

  menu: {
    type: 'Art',
    sort: 'Sortierung',
    date: 'Upload-Datum',
    duration: 'Länge',
    features: 'Merkmale',
  },

  suppression: {
    scoping: (names) =>
      `Deine Suchanfrage benutzt ${names}, was YouTube als eine andere Art von Suche beantwortet. Einen Filter dazuzunehmen schränkt die Ergebnisse nicht ein. Es mischt Videos anderer Kanäle hinein, während die Seite weiter wie eine Kanalsuche aussieht, deshalb sind die Filter unten für diese Anfrage abgeschaltet.`,
    special: (names) =>
      `Deine Suchanfrage benutzt ${names}, was überhaupt nicht als Suche beantwortet wird, deshalb greifen die Filter unten nicht und sind abgeschaltet.`,
    scopingShort: (names) =>
      `Nicht verfügbar, solange die Anfrage ${names} benutzt: zusammen liefern sie Ergebnisse anderer Kanäle.`,
    specialShort: (names) =>
      `Nicht verfügbar, solange die Anfrage ${names} benutzt, was nicht als Suche beantwortet wird.`,
    subscriptions:
      '„subscriptions:“ lässt sich hier nicht durchsuchen. Eine Suche weiß nie, wer du bist, deshalb kommt sie leer zurück statt fehlzuschlagen. Deine Abo-Seite ist der Ort dafür.',
    subscriptionsAction: 'Zu den Abos',
    channel:
      'Den Kanal selbst zu durchstöbern tut dasselbe ohne den Operator: seine Tabs Videos, Shorts, Live und Playlists blättern durch alles, was der Kanal veröffentlicht hat.',
    channelAction: 'Diesen Kanal öffnen',
  },

  allDropped: {
    title: 'Diese Ergebnisse ließen sich nicht lesen',
    detail: 'Die Instanz hat geantwortet, und nichts darin ließ sich anzeigen.',
  },

  empty: {
    title: (query) => `Keine Ergebnisse für „${query}“`,
    deadEnd:
      'Dieser Operator kommt hier immer leer zurück, das sagt also nichts darüber, was es gibt.',
    filtered: 'Mit diesen Filtern passte nichts. Ohne sie wird alles durchsucht.',
    plain:
      'Es passte nichts. Prüfe die Schreibweise, oder nimm weniger Wörter. Eine ungefähre Übereinstimmung gibt es hier nicht.',
  },

  problem: {
    notFound: {
      title: 'Diese Instanz hat keine Suche',
      detail: 'Sie wird hier nicht angeboten. Manche Instanzen lassen Teile der App weg.',
      retryable: false,
    },
    unreadable: unreadable('Die Ergebnisse ließen sich nicht lesen'),
    disabled: {
      title: 'Die Suche ist auf dieser Instanz abgeschaltet',
      detail: 'Die Administration hat sie abgeschaltet. Jede andere Seite arbeitet normal.',
      retryable: false,
    },
  },

  page: {
    resultsFor: 'Ergebnisse für',
    gridLabel: (query) => `Ergebnisse für „${query}“`,
    filtersLabel: 'Ergebnisse filtern',
    searching: (query) => `Es wird nach ${query} gesucht…`,
    exhausted: 'Das war die letzte Seite',
    exhaustedDetail: (page) =>
      `Die Instanz hat für diese Suche keine Seite ${page}. In dieser API gibt es keine Gesamtzahl, das Ende findet man also nur, indem man es erreicht.`,
    /* „Filter“ ist im Plural unverändert, die Zahl davor trägt den Numerus allein. */
    clearFilters: 'Filter zurücksetzen',
    clearFilterCount: (count) => `${count} Filter zurücksetzen`,
    backToPage: (page) => `Zurück zu Seite ${page}`,
    loadMore: (soFar) => `Mehr laden (bisher ${soFar})`,
    loadingMore: 'Wird geladen…',
    allLoaded: (count) => `Das sind alle ${count} Ergebnisse, die diese Suche geliefert hat.`,
  },
};
